import { z } from "zod";

/**
 * The people side of the panel: who a teammate is, who wrote a message,
 * who else has a conversation open, and where a browser wants its push
 * notifications delivered.
 *
 * None of this ever reaches the widget. A shopper is told *that* a person
 * is replying, never which one — an agent's name or email on a client's
 * public site is something the client never agreed to publish, and the
 * agent never agreed to either.
 */

/**
 * `users` carries an email and nothing else, so the name the panel shows is
 * derived from it: `anna.berg@shop.com` reads as "Anna Berg". Derived here,
 * once, so the inbox row, the transcript and the presence chip can't each
 * invent their own spelling of the same person.
 */
export function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local
    .split(/[._+-]+/)
    .filter((word) => word.length > 0 && !/^\d+$/.test(word));
  if (words.length === 0) return email;
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

/** A teammate as the panel needs them — enough to label a message, a row or an avatar. */
export const teamMemberDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
});
export type TeamMemberDto = z.infer<typeof teamMemberDtoSchema>;

/**
 * Who wrote an agent message (or handed a conversation back). Panel-only:
 * `messages.author_user_id` is exactly the column PLAN.md test 28 keeps off
 * the widget's wire, and that stays true — this rides on the panel's own
 * message shape, which the widget relay maps away from.
 */
export const messageAuthorDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type MessageAuthorDto = z.infer<typeof messageAuthorDtoSchema>;

// --- Presence -----------------------------------------------------------

/**
 * Panel → server: "I have this conversation open" (or no longer do).
 * Re-sent on a heartbeat, because presence rides on fire-and-forget pub/sub
 * and a viewer whose "left" was dropped must still age out.
 */
export const presenceSetSchema = z.object({
  conversationId: z.string().uuid(),
  state: z.enum(["viewing", "left"]),
});
export type PresenceSet = z.infer<typeof presenceSetSchema>;

/**
 * Server → panel, on `org:{id}`: one person's presence on one conversation.
 * The user is stamped by the server from the socket's session — a client
 * can say *where* it is, never *who* it is.
 *
 * Org-wide rather than per conversation so the inbox can show who is on
 * which row without subscribing to every thread.
 */
export const presenceUpdateSchema = z.object({
  conversationId: z.string().uuid(),
  user: messageAuthorDtoSchema,
  state: z.enum(["viewing", "left"]),
});
export type PresenceUpdate = z.infer<typeof presenceUpdateSchema>;

/** How often an open conversation re-announces itself, and how long a silent viewer is trusted for. */
export const PRESENCE_HEARTBEAT_MS = 20_000;
export const PRESENCE_TTL_MS = 50_000;

// --- Web push -----------------------------------------------------------

/** The browser's `PushSubscription.toJSON()`, narrowed to what a sender needs. */
export const pushSubscriptionBodySchema = z.object({
  endpoint: z.string().url().max(2048),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
});
export type PushSubscriptionBody = z.infer<typeof pushSubscriptionBodySchema>;

export const pushUnsubscribeBodySchema = z.object({ endpoint: z.string().url().max(2048) });
export type PushUnsubscribeBody = z.infer<typeof pushUnsubscribeBodySchema>;

/** `publicKey` is null when the deployment has no VAPID keys — the honest "push is off" rather than a flag. */
export const pushConfigDtoSchema = z.object({ publicKey: z.string().nullable() });
export type PushConfigDto = z.infer<typeof pushConfigDtoSchema>;

/**
 * What a service worker receives. Small on purpose: payloads are capped at
 * ~4KB by push services, and everything here is shown, not acted on.
 * `tag` collapses repeats — five quick messages in one conversation are one
 * notification that updates, not five.
 */
export const pushPayloadSchema = z.object({
  title: z.string(),
  body: z.string(),
  url: z.string(),
  tag: z.string(),
});
export type PushPayload = z.infer<typeof pushPayloadSchema>;
